// @ts-nocheck
// Google Maps Places Client with fallback types
import { Loader } from '@googlemaps/js-api-loader';

// CRA-style env (no import.meta)
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

let loader: Loader | null = null;
let loaderPromise: Promise<typeof google> | undefined;

const getLang = () =>
  (navigator?.language || 'en').split('-')[0];
const getRegion = () =>
  (Intl?.DateTimeFormat?.().resolvedOptions?.().locale || navigator?.language || 'US').split('-')[1] || 'US';

export async function getGoogle(): Promise<typeof google> {
  if (typeof window === 'undefined') {
    throw new Error('Google Maps can only load in the browser');
  }
  if (!API_KEY) {
    throw new Error('Missing REACT_APP_GOOGLE_MAPS_API_KEY in .env');
  }
  if (!loader) {
    loader = new Loader({
      apiKey: API_KEY,
      libraries: ['places'],
      version: 'weekly',
      language: getLang(),
      region: getRegion(),
    });
  }
  if (!loaderPromise) {
    loaderPromise = loader.load().then(() => {
      const g = (window as unknown as { google?: typeof google }).google;
      if (!g) throw new Error('Google Maps JS failed to load');
      return g;
    });
  }
  return loaderPromise!;
}

export type TextSearchBias = {
  location?: { lat: number; lng: number };
  radius?: number; // meters
};

const MAX_ATTEMPTS = 3;

function shouldRetry(status: google.maps.places.PlacesServiceStatus) {
  return (
    status === google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT ||
    status === google.maps.places.PlacesServiceStatus.UNKNOWN_ERROR ||
    status === google.maps.places.PlacesServiceStatus.INVALID_REQUEST
  );
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function backoff(attempt: number) {
  // 150ms, 300-600ms, 600-1200ms (with jitter)
  const base = 150 * Math.pow(2, attempt);
  const jitter = Math.random() * base;
  return base + jitter;
}

/** Build a stable Google Maps URL even if PlaceResult.url is missing */
export function buildMapsUrl(placeId?: string, name?: string, formattedAddress?: string) {
  if (placeId) return `https://www.google.com/maps/place/?q=place_id:${placeId}`;
  const q = [name, formattedAddress].filter(Boolean).join(' ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

export async function textSearch(
  query: string,
  bias?: TextSearchBias,
  options?: { signal?: AbortSignal }
): Promise<google.maps.places.PlaceResult[]> {
  if (!query?.trim()) return [];
  const g = await getGoogle();
  const service = new g.maps.places.PlacesService(document.createElement('div'));
  const req: google.maps.places.TextSearchRequest = {
    query,
    ...(bias?.location && { location: bias.location as google.maps.LatLngLiteral }),
    ...(bias?.radius && { radius: bias.radius }),
  };

  let attempt = 0;
  while (true) {
    if (options?.signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    const results = await new Promise<{
      results?: google.maps.places.PlaceResult[];
      status: google.maps.places.PlacesServiceStatus;
    }>((resolve) => {
      service.textSearch(req, (res, status) => resolve({ results: res || undefined, status }));
    });

    if (results.status === g.maps.places.PlacesServiceStatus.OK && results.results) {
      return results.results;
    }
    if (results.status === g.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
      return [];
    }
    if (shouldRetry(results.status) && attempt < MAX_ATTEMPTS - 1) {
      attempt++;
      await sleep(backoff(attempt));
      continue;
    }
    throw new Error(`Places textSearch failed (${results.status}) for query="${query}"`);
  }
}

export async function getDetails(
  placeId: string,
  options?: { signal?: AbortSignal }
): Promise<google.maps.places.PlaceResult | null> {
  if (!placeId) return null;
  const g = await getGoogle();
  const service = new g.maps.places.PlacesService(document.createElement('div'));
  const req: google.maps.places.PlaceDetailsRequest = {
    placeId,
    fields: [
      'place_id',
      'name',
      'formatted_address',
      'international_phone_number',
      'website',
      'url',
      'photos',
      'geometry',
      'types',
      'rating',
      'user_ratings_total',
  'opening_hours',
    ],
  };

  let attempt = 0;
  while (true) {
    if (options?.signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    const { result, status } = await new Promise<{
      result?: google.maps.places.PlaceResult;
      status: google.maps.places.PlacesServiceStatus;
    }>((resolve) => {
      service.getDetails(req, (r, s) => resolve({ result: r || undefined, status: s }));
    });

    if (status === g.maps.places.PlacesServiceStatus.OK && result) {
      return result;
    }
    if (status === g.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
      return null;
    }
    if (shouldRetry(status) && attempt < MAX_ATTEMPTS - 1) {
      attempt++;
      await sleep(backoff(attempt));
      continue;
    }
    throw new Error(`Places getDetails failed (${status}) for placeId="${placeId}"`);
  }
}

/** Simple client-side Geocoder helper */
export async function geocodeAddress(address: string): Promise<google.maps.LatLngLiteral | null> {
  if (!address?.trim()) return null;
  const g = await getGoogle();
  const geocoder = new g.maps.Geocoder();

  const { results, status } = await new Promise<{ results: google.maps.GeocoderResult[]; status: google.maps.GeocoderStatus }>((resolve) => {
    geocoder.geocode({ address }, (r, s) => resolve({ results: r || [], status: s as google.maps.GeocoderStatus }));
  });

  if (status === g.maps.GeocoderStatus.OK && results[0]?.geometry?.location) {
    const loc = results[0].geometry.location;
    return { lat: loc.lat(), lng: loc.lng() };
  }
  if (status === g.maps.GeocoderStatus.ZERO_RESULTS) return null;
  throw new Error(`Geocoder failed (${status}) for address="${address}"`);
}
